import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';

const FAQ = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const faqs = [
    {
      question: 'What is your typical project timeline?',
      answer:
        'Project timelines vary based on scope and complexity. A bathroom remodel typically takes 2-4 weeks, kitchen renovations 4-8 weeks, and full home renovations can take 2-4 months. During your free consultation, we provide a detailed timeline specific to your project.',
    },
    {
      question: 'Do you handle permits and inspections?',
      answer:
        'Yes, we handle all necessary permits and coordinate inspections as part of our comprehensive service. We ensure your renovation project complies with all local building codes and regulations in Montgomery County and surrounding areas.',
    },
    {
      question: 'What areas do you serve?',
      answer:
        'We serve Gaithersburg, Rockville, Bethesda, Potomac, Silver Spring, Germantown, Clarksburg, Damascus, and surrounding Maryland areas. If you don\'t see your area listed, please contact us—we may still be able to help with your project.',
    },
    {
      question: 'Do you offer free estimates?',
      answer:
        'Absolutely! We provide free in-home consultations where we discuss your vision, assess the space, and provide a detailed estimate. There\'s no obligation, and you\'ll receive a comprehensive breakdown of costs and timeline.',
    },
    {
      question: 'What warranties do you provide?',
      answer:
        'We stand behind our work with a 5-year warranty on craftsmanship. Additionally, all materials and products come with their respective manufacturer warranties. We believe in quality that lasts.',
    },
    {
      question: 'Can I stay in my home during the renovation?',
      answer:
        'In most cases, yes. We work efficiently to minimize disruption to your daily life. For larger projects, we can discuss the best approach during your consultation. We always maintain a clean, safe work environment.',
    },
    {
      question: 'How do payments work?',
      answer:
        'We typically structure payments in phases: a deposit to begin, progress payments at key milestones, and final payment upon completion. We provide clear payment schedules upfront so there are no surprises.',
    },
    {
      question: 'Do you help with design decisions?',
      answer:
        'Yes! While we\'re not interior designers, our team has extensive experience and can provide guidance on layouts, materials, and finishes. We also work well with interior designers if you have one on your project.',
    },
  ];

  return (
    <section
      id="faq"
      data-testid="faq-section"
      ref={ref}
      className="section-padding bg-[#F4F4F5]"
    >
      <div className="container-custom max-w-4xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <p className="text-[#D4AF37] text-sm font-medium tracking-[0.2em] uppercase mb-4">
            Questions & Answers
          </p>
          <h2
            data-testid="faq-title"
            className="font-serif text-3xl md:text-4xl lg:text-5xl text-[#0A0A0A] font-bold mb-6"
          >
            Frequently Asked Questions
          </h2>
          <div className="w-16 h-0.5 bg-[#D4AF37] mx-auto mb-6" />
          <p className="text-[#71717A] max-w-2xl mx-auto">
            Find answers to common questions about our renovation services,
            process, and what to expect when working with Lux Renovations.
          </p>
        </motion.div>

        {/* FAQ Accordion */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                data-testid={`faq-item-${index}`}
                className="bg-white border border-[#E4E4E7] px-6"
              >
                <AccordionTrigger
                  data-testid={`faq-trigger-${index}`}
                  className="text-left font-serif text-lg text-[#0A0A0A] hover:text-[#D4AF37] hover:no-underline py-6"
                >
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent
                  data-testid={`faq-content-${index}`}
                  className="text-[#71717A] leading-relaxed pb-6"
                >
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="text-center mt-12"
        >
          <p className="text-[#71717A] mb-4">Still have questions?</p>
          <a
            href="#contact"
            onClick={(e) => {
              e.preventDefault();
              document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
            }}
            data-testid="faq-contact-link"
            className="text-[#D4AF37] font-medium hover:underline"
          >
            Contact us for more information →
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default FAQ;
